import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Tabs,
  Tab,
  Button,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress
} from '@mui/material';
import {
  Edit as EditIcon,
  TrendingUp as TrendingUpIcon,
  Message as MessageIcon,
  Timeline as TimelineIcon,
  Add as AddIcon,
  Assessment as AssessmentIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';
import { toastMessage } from '../../utils/toastMessage';
import {
  getNotificationMessagesApi,
  createNotificationMessageApi,
  updateNotificationMessageApi,
  getNotificationBatchesApi,
  getConversionStatsApi
} from '../../services/notificationManagement/enhancedNotificationService';

const FollowupManagement = () => {
  const followupCampaignPrefix = 'First Consult Followup';
  const astrologerCampaignPrefix = 'Astrologer of the Day Followup';
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [astrologerLoading, setAstrologerLoading] = useState(false);
  const [messages, setMessages] = useState([]);
  const [batches, setBatches] = useState([]);
  const [stats, setStats] = useState(null);
  const [astrologerStats, setAstrologerStats] = useState(null);
  const [astrologerBatch, setAstrologerBatch] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editMessage, setEditMessage] = useState(null);

  const [formData, setFormData] = useState({
    title: '',
    body: '',
    tags: '', // will be hours and concern
    isActive: true
  });

  const [selectedTimeframe, setSelectedTimeframe] = useState('');
  const [selectedConcern, setSelectedConcern] = useState('');

  const concerns = [
    'General', 'Relationship', 'Career', 'Finance',
    'Health', 'Marriage', 'Depression', 'Family', 'Exam'
  ];

  const timeframes = ['24h', '72h', '144h'];

  useEffect(() => {
    if (activeTab === 0) {
      fetchMessages();
    } else if (activeTab === 1) {
      fetchAnalytics();
    } else {
      fetchAstrologerAnalytics();
    }
  }, [activeTab]);

  const fetchMessages = async () => {
    setLoading(true);
    try {
      const res = await getNotificationMessagesApi({
        category: 'first_consult_followup',
        limit: 100
      });
      if (res.data.success) {
        setMessages(res.data.data.messages);
      }
    } catch (error) {
      console.error(error);
      toastMessage('Error fetching messages', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalytics = async () => {
    setAnalyticsLoading(true);
    try {
      const [batchesRes, statsRes] = await Promise.all([
        getNotificationBatchesApi({ limit: 10, campaignPrefix: followupCampaignPrefix }),
        getConversionStatsApi({ campaignPrefix: followupCampaignPrefix })
      ]);

      if (batchesRes.data.success) setBatches(batchesRes.data.data.batches);
      if (statsRes.data.success) setStats(statsRes.data.data);
    } catch (error) {
      console.error(error);
      toastMessage('Error fetching analytics', 'error');
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const fetchAstrologerAnalytics = async () => {
    setAstrologerLoading(true);
    try {
      const [batchesRes, statsRes] = await Promise.all([
        getNotificationBatchesApi({ limit: 5, campaignPrefix: astrologerCampaignPrefix }),
        getConversionStatsApi({ campaignPrefix: astrologerCampaignPrefix })
      ]);
      if (batchesRes.data.success) setAstrologerBatch(batchesRes.data.data.batches?.[0] || null);
      if (statsRes.data.success) setAstrologerStats(statsRes.data.data);
    } catch (error) {
      console.error(error);
      toastMessage('Error fetching Astrologer of the Day tracking', 'error');
    } finally {
      setAstrologerLoading(false);
    }
  };

  const formatIst = (value) => {
    if (!value) return 'Not scheduled';
    return new Date(value).toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata',
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleOpenDialog = (msg = null) => {
    if (msg) {
      setEditMessage(msg);
      setFormData({
        title: msg.title,
        body: msg.body,
        tags: msg.tags.join(', '),
        isActive: msg.isActive
      });

      // Parse tags for dropdowns
      const timeframeMatch = timeframes.find(t => msg.tags.includes(t));
      const concernMatch = concerns.find(c => msg.tags.includes(c.toLowerCase()));
      setSelectedTimeframe(timeframeMatch || '');
      setSelectedConcern(concernMatch || '');
    } else {
      setEditMessage(null);
      setFormData({
        title: '',
        body: '',
        tags: '',
        isActive: true
      });
      setSelectedTimeframe('');
      setSelectedConcern('');
    }
    setOpenDialog(true);
  };
  const handleSubmit = async () => {
    if (!formData.title || !formData.body || !selectedTimeframe || !selectedConcern) {
      toastMessage('All fields including Timeframe and Concern are required', 'error');
      return;
    }

    const finalTags = [selectedTimeframe, selectedConcern.toLowerCase()].filter(Boolean);

    const payload = {
      ...formData,
      category: 'first_consult_followup',
      tags: finalTags,
      timing: 'anytime',
      priority: 1,
      language: 'en',
      targetAudience: 'all'
    };

    try {
      if (editMessage) {
        await updateNotificationMessageApi(editMessage._id, payload);
        toastMessage('Message updated successfully', 'success');
      } else {
        await createNotificationMessageApi(payload);
        toastMessage('Message created successfully', 'success');
      }
      setOpenDialog(false);
      fetchMessages();
    } catch (error) {
      console.error(error);
      toastMessage('Error saving message', 'error');
    }
  };

  const renderMessageConfig = () => (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, alignItems: 'center' }}>
        <Typography variant="h6">Follow-up Message Configuration</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          sx={{ bgcolor: '#ff8989', '&:hover': { bgcolor: '#ff6b6b' } }}
        >
          Add New Template
        </Button>
      </Box>

      <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
        <Table>
          <TableHead sx={{ bgcolor: '#f8f9fa' }}>
            <TableRow>
              <TableCell>Title</TableCell>
              <TableCell>Content Snippet</TableCell>
              <TableCell>Target (Tags)</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={5} align="center"><CircularProgress size={24} /></TableCell></TableRow>
            ) : messages.length === 0 ? (
              <TableRow><TableCell colSpan={5} align="center">No followup templates found</TableCell></TableRow>
            ) : messages.map((msg) => (
              <TableRow key={msg._id}>
                <TableCell sx={{ fontWeight: 'medium' }}>{msg.title}</TableCell>
                <TableCell sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {msg.body}
                </TableCell>
                <TableCell>
                  {msg.tags.map(tag => (
                    <Chip key={tag} label={tag} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                  ))}
                </TableCell>
                <TableCell>
                  <Chip
                    label={msg.isActive ? 'Active' : 'Inactive'}
                    color={msg.isActive ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell align="right">
                  <IconButton onClick={() => handleOpenDialog(msg)} size="small">
                    <EditIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );

  const renderAnalytics = () => {
    const isInitialAnalyticsLoad = analyticsLoading && !stats && batches.length === 0;

    if (isInitialAnalyticsLoad) {
      return (
        <Paper
          sx={{
            p: 5,
            minHeight: 280,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 2
          }}
        >
          <Box sx={{ textAlign: 'center' }}>
            <CircularProgress size={36} sx={{ color: '#ff8989', mb: 2 }} />
            <Typography variant="subtitle1" fontWeight="medium">
              Loading analytics...
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Fetching campaign conversion stats
            </Typography>
          </Box>
        </Paper>
      );
    }

    return (
    <Box sx={{ position: 'relative' }}>
      {analyticsLoading && (
        <LinearProgress
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 2,
            mb: 2,
            borderRadius: 1,
            '& .MuiLinearProgress-bar': { bgcolor: '#ff8989' }
          }}
        />
      )}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={fetchAnalytics}
          disabled={analyticsLoading}
          sx={{ borderColor: '#ff8989', color: '#ff8989' }}
        >
          Refresh
        </Button>
      </Box>

      {/* Top Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} md={2.4}>
          <Card sx={{ borderRadius: 3, borderLeft: '5px solid #ff9800' }}>
            <CardContent>
              <Typography color="textSecondary" variant="subtitle2" gutterBottom>Total Targeted</Typography>
              <Typography variant="h4">{stats?.overall?.totalTargeted || 0}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={2.4}>
          <Card sx={{ borderRadius: 3, borderLeft: '5px solid #4caf50' }}>
            <CardContent>
              <Typography color="textSecondary" variant="subtitle2" gutterBottom>Total Sent</Typography>
              <Typography variant="h4">{stats?.overall?.totalSent || 0}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={2.4}>
          <Card sx={{ borderRadius: 3, borderLeft: '5px solid #ffeb3b' }}>
            <CardContent>
              <Typography color="textSecondary" variant="subtitle2" gutterBottom>Consultation Attempts</Typography>
              <Typography variant="h4" sx={{ color: '#fbc02d' }}>{stats?.overall?.totalConsulted || 0}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={2.4}>
          <Card sx={{ borderRadius: 3, borderLeft: '5px solid #2196f3' }}>
            <CardContent>
              <Typography color="textSecondary" variant="subtitle2" gutterBottom>Paid Sales</Typography>
              <Typography variant="h4" sx={{ color: '#2196f3' }}>{stats?.overall?.totalPurchased || 0}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={2.4}>
          <Card sx={{ borderRadius: 3, borderLeft: '5px solid #9c27b0' }}>
            <CardContent>
              <Typography color="textSecondary" variant="subtitle2" gutterBottom>Conv. Rate</Typography>
              <Typography variant="h4" sx={{ color: '#9c27b0' }}>
                {stats?.overall?.totalSent > 0
                  ? ((stats.overall.totalPurchased / stats.overall.totalSent) * 100).toFixed(1)
                  : 0}%
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Timeframe-wise breakdown */}
      <Typography variant="h6" sx={{ mb: 2 }}>Timeframe Performance Analysis (24h, 72h, 144h)</Typography>
      <Grid container spacing={2} sx={{ mb: 4 }}>
        {['24h', '72h', '144h'].map((time) => {
          const t = stats?.timeframeStats?.find(item => item._id === time) || { sent: 0, consulted: 0, purchased: 0 };
          return (
            <Grid item xs={12} md={4} key={time}>
              <Card variant="outlined" sx={{ borderRadius: 2, bgcolor: '#fcfcfc', border: '1px solid #eee' }}>
                <CardContent sx={{ pb: '16px !important' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, alignItems: 'center' }}>
                    <Typography variant="h6" sx={{ color: '#ff8989', fontWeight: 'bold' }}>
                      {time} Follow-up
                    </Typography>
                    <Chip 
                      label={`${t.sent > 0 ? ((t.purchased / t.sent) * 100).toFixed(1) : 0}% Conv.`} 
                      size="small" 
                      sx={{ bgcolor: '#4caf50', color: 'white', fontWeight: 'bold' }} 
                    />
                  </Box>
                  
                  <Grid container spacing={2}>
                    <Grid item xs={4}>
                      <Typography variant="caption" color="textSecondary">Sent</Typography>
                      <Typography variant="h6">{t.sent}</Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="caption" color="textSecondary">Attempts</Typography>
                      <Typography variant="h6" sx={{ color: '#fbc02d' }}>{t.consulted}</Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="caption" color="textSecondary">Paid Sales</Typography>
                      <Typography variant="h6" sx={{ color: '#4caf50' }}>{t.purchased}</Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Concern-wise breakdown */}
      <Typography variant="h6" sx={{ mb: 2 }}>Concern-wise Conversion Analysis</Typography>
      <Grid container spacing={2} sx={{ mb: 4 }}>
        {stats?.concernStats?.map((c) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={c._id}>
            <Card variant="outlined" sx={{ borderRadius: 2, height: '100%', transition: 'transform 0.2s', '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 4px 20px rgba(0,0,0,0.08)' } }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, alignItems: 'center' }}>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ color: '#333', textTransform: 'capitalize' }}>
                    {c._id || 'General'}
                  </Typography>
                  <Chip
                    label={`${c.sent > 0 ? ((c.purchased / c.sent) * 100).toFixed(1) : 0}%`}
                    size="small"
                    sx={{ bgcolor: '#ff8989', color: 'white', fontWeight: 'bold' }}
                  />
                </Box>

                <Grid container spacing={1}>
                  <Grid item xs={4}>
                    <Typography variant="caption" color="textSecondary" display="block">Sent</Typography>
                    <Typography variant="body2" fontWeight="bold">{c.sent}</Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="caption" color="textSecondary" display="block">Attempts</Typography>
                    <Typography variant="body2" fontWeight="bold" sx={{ color: '#fbc02d' }}>{c.consulted || 0}</Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="caption" color="textSecondary" display="block">Paid Sales</Typography>
                    <Typography variant="body2" fontWeight="bold" sx={{ color: '#4caf50' }}>{c.purchased}</Typography>
                  </Grid>
                </Grid>

                <Box sx={{ mt: 2 }}>
                  <LinearProgress
                    variant="determinate"
                    value={c.sent > 0 ? Math.min((c.purchased / c.sent) * 100, 100) : 0}
                    sx={{ height: 6, borderRadius: 3, bgcolor: '#f0f0f0', '& .MuiLinearProgress-bar': { bgcolor: '#ff8989' } }}
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Detailed Rates Table */}
      {/* <Box sx={{ mb: 4 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Conversion Performance by Concern</Typography>
        <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
          <Table size="small">
            <TableHead sx={{ bgcolor: '#f8f9fa' }}>
              <TableRow>
                <TableCell>Concern</TableCell>
                <TableCell align="center">Total Sent</TableCell>
                <TableCell align="center">Successful Purchases</TableCell>
                <TableCell align="right">Conversion Rate</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {stats?.concernStats?.length > 0 ? (
                stats.concernStats.map((c) => (
                  <TableRow key={c._id}>
                    <TableCell sx={{ textTransform: 'capitalize', fontWeight: 'medium' }}>{c._id || 'General'}</TableCell>
                    <TableCell align="center">{c.sent}</TableCell>
                    <TableCell align="center">{c.purchased}</TableCell>
                    <TableCell align="right">
                      <Chip 
                        label={`${c.sent > 0 ? ((c.purchased / c.sent) * 100).toFixed(1) : 0}%`}
                        size="small"
                        variant="outlined"
                        sx={{ fontWeight: 'bold', color: '#ff8989', borderColor: '#ff8989' }}
                      />
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={4} align="center">No data available yet</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box> */}

      {/* Recent Batches Table */}
      {/* <Typography variant="h6" sx={{ mb: 2 }}>Recent Campaign Batches</Typography>
      <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
        <Table size="small">
          <TableHead sx={{ bgcolor: '#f8f9fa' }}>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Campaign</TableCell>
              <TableCell align="center">Targeted</TableCell>
              <TableCell align="center">Sent</TableCell>
              <TableCell align="center">Paid Sales</TableCell>
              <TableCell align="right">Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {analyticsLoading && batches.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <CircularProgress size={24} sx={{ color: '#ff8989' }} />
                </TableCell>
              </TableRow>
            ) : batches.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">No campaign batches found</TableCell>
              </TableRow>
            ) : batches.map((batch) => (
              <TableRow key={batch._id}>
                <TableCell>{new Date(batch.createdAt).toLocaleDateString()}</TableCell>
                <TableCell sx={{ fontWeight: 'medium' }}>{batch.campaignName}</TableCell>
                <TableCell align="center">{batch.totalTargeted}</TableCell>
                <TableCell align="center">{batch.totalSent}</TableCell>
                <TableCell align="center" sx={{ color: batch.totalPurchased > 0 ? 'success.main' : 'inherit', fontWeight: 'bold' }}>
                  {batch.totalPurchased}
                </TableCell>
                <TableCell align="right">
                  <Chip
                    label={batch.status === 'completed' ? 'Success' : batch.status}
                    size="small"
                    color={batch.status === 'completed' ? 'success' : 'warning'}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer> */}
    </Box>
  );
  };

  const renderAstrologerCampaign = () => {
    const byStatus = astrologerBatch?.metadata?.byStatus || {};
    const pending = (byStatus.pending || 0) + (byStatus.processing || 0);
    const sent = astrologerStats?.overall?.totalSent || astrologerBatch?.totalSent || 0;
    const failed = astrologerStats?.overall?.totalFailed || astrologerBatch?.totalFailed || 0;
    const targeted = astrologerStats?.overall?.totalTargeted || astrologerBatch?.totalTargeted || 0;
    const consulted = astrologerStats?.overall?.totalConsulted || astrologerBatch?.totalConsulted || 0;
    const purchased = astrologerStats?.overall?.totalPurchased || astrologerBatch?.totalPurchased || 0;
    const suppressed = byStatus.suppressed || 0;

    if (astrologerLoading && !astrologerBatch && !astrologerStats) {
      return (
        <Paper sx={{ p: 5, minHeight: 280, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 2 }}>
          <Box sx={{ textAlign: 'center' }}>
            <CircularProgress size={36} sx={{ color: '#ff8989', mb: 2 }} />
            <Typography variant="subtitle1" fontWeight="medium">Loading campaign tracking...</Typography>
          </Box>
        </Paper>
      );
    }

    return (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, gap: 2, alignItems: 'center' }}>
          <Box>
            <Typography variant="h6">Astrologer of the Day</Typography>
            <Typography variant="body2" color="textSecondary">
              One time on 5 Nov 2026 at 1:30 PM IST. Then one user every 10 minutes. Users with 2 or more completed consultations.
              Title is the consultant name. A tap opens that consultation chat.
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchAstrologerAnalytics}
            disabled={astrologerLoading}
            sx={{ borderColor: '#ff8989', color: '#ff8989', flexShrink: 0 }}
          >
            Refresh
          </Button>
        </Box>

        {astrologerLoading && <LinearProgress sx={{ mb: 2, borderRadius: 1, '& .MuiLinearProgress-bar': { bgcolor: '#ff8989' } }} />}

        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[
            ['Targeted', targeted, '#ff9800'],
            ['Sent', sent, '#4caf50'],
            ['Pending', pending, '#2196f3'],
            ['Failed', failed, '#f44336'],
            ['Skipped', suppressed, '#9e9e9e'],
            ['Consultations after send', consulted, '#fbc02d'],
            ['Paid sales', purchased, '#7b1fa2']
          ].map(([label, value, color]) => (
            <Grid item xs={12} sm={6} md={3} key={label}>
              <Card sx={{ borderRadius: 3, borderLeft: `5px solid ${color}` }}>
                <CardContent>
                  <Typography color="textSecondary" variant="subtitle2" gutterBottom>{label}</Typography>
                  <Typography variant="h4">{value}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Card variant="outlined" sx={{ borderRadius: 2, mb: 3 }}>
          <CardContent>
            <Typography variant="subtitle2" color="textSecondary">Next send</Typography>
            <Typography variant="h6" sx={{ mb: 2 }}>{formatIst(astrologerBatch?.metadata?.nextSendAt)}</Typography>
            <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>
              Queue status: {astrologerBatch?.metadata?.queueStatus || 'Waiting for 1:30 PM IST'}
              {astrologerBatch?.metadata?.lastQueueDate ? ` · last queue ${astrologerBatch.metadata.lastQueueDate}` : ''}
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-line', bgcolor: '#fff9f9', p: 2, borderRadius: 1 }}>
              {`{Username}, Aapki kundali se judi ek zaroori baat share karni thi. Grahon ki sthiti mein badlav ho chuke hain, aur inka asar aapke jeevan ke alag-alag pehluon par pad sakta hai. Aaiye, jaante hain ye badlav aapke liye kya naye sanket lekar aaye hain.\n\nEk khushkhabri bhi hai! 🏆 Mujhe 'Astrologer of the Day' award mila hai, aur is khushi ko aapke saath celebrate karne ke liye aaj meri taraf se 20% OFF consultation par!\n\nUse Coupon : "MYVEDIC20"`}
            </Typography>
            <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 1 }}>
              Notification title: consultant name. Coupon MYVEDIC20. Conversion counts a consultation or paid session started after the message was sent.
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
        <Box sx={{ p: 1, borderRadius: 2, bgcolor: 'rgba(255, 137, 137, 0.1)', color: '#ff8989' }}>
          <TimelineIcon fontSize="large" />
        </Box>
        <Box>
          <Typography variant="h5" fontWeight="bold">Automated Follow-up Management</Typography>
          <Typography variant="body2" color="textSecondary">Manage 24h, 72h, 144h concern-based notifications and track their performance.</Typography>
        </Box>
      </Box>

      <Card sx={{ mb: 3, borderRadius: 2 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} textColor="primary" indicatorColor="primary">
            <Tab label="Messages & Templates" icon={<MessageIcon />} iconPosition="start" />
            <Tab label="Tracking & Analysis" icon={<AssessmentIcon />} iconPosition="start" />
            <Tab label="Astrologer of the Day" icon={<TrendingUpIcon />} iconPosition="start" />
          </Tabs>
        </Box>
      </Card>

      {activeTab === 0 ? renderMessageConfig() : activeTab === 1 ? renderAnalytics() : renderAstrologerCampaign()}

      {/* Message Editor Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#f8f9fa' }}>
          {editMessage ? 'Edit Follow-up Template' : 'Add New Follow-up Template'}
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <TextField
            fullWidth
            label="Template Title (e.g., Relationship 24h)"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            margin="normal"
            size="small"
          />
          <TextField
            fullWidth
            label="Message Body"
            multiline
            rows={4}
            value={formData.body}
            onChange={(e) => setFormData({ ...formData, body: e.target.value })}
            margin="normal"
            size="small"
            placeholder="Hi {name}, we noticed you had a session about {concern}..."
          />
          <Box sx={{ mt: 2, p: 2, bgcolor: '#fff9f9', borderRadius: 1, border: '1px dashed #ff8989' }}>
            <Typography variant="caption" color="textSecondary" sx={{ mb: 2, display: 'block' }}>
              <strong>Follow-up Targeting:</strong> Select the timeframe and specific concern this message should target.
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Timeframe</InputLabel>
                  <Select
                    value={selectedTimeframe}
                    label="Timeframe"
                    onChange={(e) => setSelectedTimeframe(e.target.value)}
                  >
                    {timeframes.map(t => (
                      <MenuItem key={t} value={t}>{t}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Target Concern</InputLabel>
                  <Select
                    value={selectedConcern}
                    label="Target Concern"
                    onChange={(e) => setSelectedConcern(e.target.value)}
                  >
                    {concerns.map(c => (
                      <MenuItem key={c} value={c}>{c}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, bgcolor: '#f8f9fa' }}>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            sx={{ bgcolor: '#ff8989', '&:hover': { bgcolor: '#ff6b6b' } }}
          >
            {editMessage ? 'Update Template' : 'Create Template'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FollowupManagement;
